import { useState } from 'react'
import { ContainerWrapper } from '../Container'
import SectionHeadingWrapper from './SectionHeadingWrapper'
import SectionHeading from './SectionHeading'

const ACCENTS = {
    orange: {
        border: 'border-[#ED5C2F]',
        text: 'text-[#ED5C2F]',
        chevron: '#ED5C2F',
        bgAnswer: 'bg-[#ED5C2F]',
        shadow: 'shadow-[0_2px_7px_0_rgba(0,0,0,0.36)]',
    },
    blue: {
        border: 'border-[#2f6bff]',
        text: 'text-[#2f6bff]',
        chevron: '#2f6bff]',
        bgAnswer: 'bg-[#fff5ec]',
        shadow: 'shadow-[0_10px_24px_rgba(47,107,255,0.10)]',
    },
}

const Chevron = ({ open, color = '#f05a2b' }) => (
    <svg
        width="20"
        height="20"
        viewBox="0 0 512 512"
        aria-hidden="true"
        fill={color}
        className={`transition-transform h-5 w-5 ${
            open ? 'rotate-180' : 'rotate-0'
        }`}
        xmlns="http://www.w3.org/2000/svg">
        <path d="M504 256c0 137-111 248-248 248S8 393 8 256 119 8 256 8s248 111 248 248zM273 369.9l135.5-135.5c9.4-9.4 9.4-24.6 0-33.9l-17-17c-9.4-9.4-24.6-9.4-33.9 0L256 285.1 154.4 183.5c-9.4-9.4-24.6-9.4-33.9 0l-17 17c-9.4 9.4-9.4 24.6 0 33.9L239 369.9c9.4 9.4 24.6 9.4 34 0z"></path>
    </svg>
)

function FAQItem({ item, idx, isOpen, toggle }) {
    const accent = ACCENTS['orange']

    return (
        <div
            className={`rounded-xl overflow-hidden ${accent.shadow} ${
                isOpen ? 'bg-white' : `border-2 ${accent.border} bg-white`
            } transition`}>
            {/* Header button */}
            <button
                type="button"
                aria-expanded={isOpen}
                aria-controls={`faq-panel-${idx}`}
                id={`faq-button-${idx}`}
                onClick={toggle}
                className={`w-full flex items-center justify-between text-left ${
                    isOpen ? 'px-5 py-4' : 'px-5 py-3'
                }`}>
                <span
                    className={`w-[92%] md:[100%] md:max-w-[466px] font-semibold text-xl ${accent.text}`}>
                    {item.question}
                </span>
                <Chevron open={isOpen} color={accent.chevron} />
            </button>

            {/* Answer panel */}
            <div
                id={`faq-panel-${idx}`}
                role="region"
                aria-labelledby={`faq-button-${idx}`}
                className={`grid transition-all duration-200 ease-out ${
                    isOpen
                        ? 'grid-rows-[1fr] opacity-100 px-5 bg-[linear-gradient(180deg,#FED7AA00_0%,#FFF8E9_100%)]'
                        : 'grid-rows-[0fr] opacity-0 px-0'
                }`}>
                <div
                    className={`overflow-hidden ${
                        isOpen ? 'mb-5 py-4' : 'mb-0'
                    }`}>
                    <div
                        className="text-black text-lg font-normal [&_strong]:font-semibold [&_a]:underline"
                        dangerouslySetInnerHTML={{ __html: item.answerHtml }}
                    />
                </div>
            </div>
        </div>
    )
}

export default function RealEstateFAQs({
    heading = 'Frequently Asked Questions',
    itemsLeft = defaultItemsLeft,
    itemsRight = defaultItemsRight,
}) {
    // independent open state per column
    const [openLeft, setOpenLeft] = useState(() => {
        const s = new Set()
        itemsLeft.forEach((it, i) => it?.defaultOpen && s.add(i))
        return s
    })
    const [openRight, setOpenRight] = useState(() => {
        const s = new Set()
        itemsRight.forEach((it, i) => it?.defaultOpen && s.add(i))
        return s
    })

    const toggleLeft = (idx) =>
        setOpenLeft((prev) => {
            const copy = new Set(prev)
            if (copy.has(idx)) copy.delete(idx)
            else copy.add(idx)
            return copy
        })

    const toggleRight = (idx) =>
        setOpenRight((prev) => {
            const copy = new Set(prev)
            if (copy.has(idx)) copy.delete(idx)
            else copy.add(idx)
            return copy
        })

    return (
        <section aria-labelledby="faqs-heading" className="py-16 px-2">
            <ContainerWrapper footer={false}>
                <SectionHeadingWrapper>
                    <SectionHeading>{heading}</SectionHeading>
                </SectionHeadingWrapper>

                {/* Two independent columns */}
                <div className="md:flex max-w-[1140px] mx-auto">
                    <div className="md:w-1/2 p-2 space-y-6">
                        {itemsLeft.map((item, idx) => (
                            <FAQItem
                                key={`L-${idx}`}
                                item={item}
                                idx={`L-${idx}`}
                                isOpen={openLeft.has(idx)}
                                toggle={() => toggleLeft(idx)}
                            />
                        ))}
                    </div>
                    <div className="md:w-1/2 p-2 space-y-6 md:mt-0">
                        {itemsRight.map((item, idx) => (
                            <FAQItem
                                key={`R-${idx}`}
                                item={item}
                                idx={`R-${idx}`}
                                isOpen={openRight.has(idx)}
                                toggle={() => toggleRight(idx)}
                            />
                        ))}
                    </div>
                </div>
            </ContainerWrapper>
        </section>
    )
}

// Defaults split into two columns to match the visual
const defaultItemsLeft = [
    {
        question:
            'How effective are postcards compared to email and cold calls?',
        answerHtml:
            'Direct mail commands <strong>80–90% open rates</strong>, vastly outperforming email, which sees just 20–30%. Plus, campaigns involving postcards report <strong>response rates of 4–9%</strong>, versus under 1% for most digital channels. That means postcards reliably deliver attention and action—making them a potent tool for real estate investor outreach.',
        accent: 'orange',
        defaultOpen: true,
    },
    {
        question: 'Does personalization really matter?',
        answerHtml:
            'Personalization (e.g., merge fields, real‑ink fonts) consistently lifts engagement and response.',
        accent: 'orange',
    },
    {
        question: 'How many mailings does it take to get a deal?',
        answerHtml:
            'Most investors see best results with <strong>multi‑touch</strong> sequences (3–6 touches) to prime response.',
        accent: 'orange',
    },
    {
        question: 'What kind of ROI can I expect from postcards?',
        answerHtml:
            'ROI varies by list quality and offer, but investors often report higher ROI than single‑channel digital alone.',
        accent: 'orange',
    },
    {
        question: 'What else can I track in my campaign?',
        answerHtml:
            'Track response rates, call volumes, conversions, and attribution by list and creative to optimize ROI.',
        accent: 'orange',
    },
]

const defaultItemsRight = [
    {
        question:
            'Are real estate postcard response rates higher than standard averages?',
        answerHtml:
            'Yes—while general direct mail among real estate averages around <strong>3.3% response rate</strong>, investor‑focused, multi‑touch postcard campaigns typically range between <strong>4% and 9%</strong>, depending on targeting, message, and follow‑up strategy.',
        accent: 'blue',
        defaultOpen: true,
    },
    {
        question: 'Why does direct mail get better engagement?',
        answerHtml:
            'Physical mail stands out, is harder to ignore, and builds trust through tangible touch points.',
        accent: 'orange',
    },
    {
        question: 'What about speed to market, is printing and mailing slow?',
        answerHtml:
            'With the right partner, <strong>turnarounds can average ~3 days</strong> from approval to in‑the‑mail.',
        accent: 'orange',
    },
    {
        question: "I'm new to direct mail—will I get help?",
        answerHtml:
            'Yes. Expert guidance helps with messaging, list strategy, and cadence to maximize outcomes.',
        accent: 'orange',
    },
    {
        question: 'How do I get started?',
        answerHtml:
            'Pick a design, target your list, launch your campaign, then track results—our team can support every step.',
        accent: 'orange',
    },
]
